/** @format */

import { APPLICATION_STAGE } from "../../common/models/enums/applicationStage";
import { IState } from "../../common/models/interfaces/IState";
import {
  AllApplicantDto,
  DocumentsViewDto,
  LegalGuardianDetailDto,
  PersonalDetailsDto,
} from "../applicant/Models";

export interface ExportState extends IState {
  // Inherits status: 'idle' | 'loading' | 'failed' from IState
  // Inherits error: Validation | any from IState
  isExporting?: boolean;
}

export interface AdminInitialState {
  applicantState: AdminApplicantState;
  applicationDatesState: ApplicationDateState;
  assessmentAuditsState: AssessmentAuditsState;
  adminApplicantDeclineState: AdminApplicantDeclineState;
  applicantDocumentState: ApplicantDocumentState;
  dashboardState: DasboardState;
  exportState: ExportState;
}

export interface DasboardState extends IState {
  dashboard: DasboardDto;
  notifications: NotificationDto[];
  markAsRead: number;
  /** Last requested intake year for dashboard; stale async completions are ignored. */
  latestDashboardYearRequest?: number;
}
export interface DasboardDto {
  status: StatusDto[];
  race: StatusDto[];
  universities: StatusDto[];
  totalInCompleted: number;
  returningApplicants: number;
  totalAssessed: number;
  allApplications: number;
  femaleApplicants  : number;
  maleApplicants: number;
  applicationsByYearOfStudy?: YearOfStudyCountsDto;
  incompleteByYearOfStudy?: YearOfStudyCountsDto;
  submittedByYearOfStudy?: YearOfStudyCountsDto;
  underReviewByYearOfStudy?: YearOfStudyCountsDto;
  assessedByYearOfStudy?: YearOfStudyCountsDto;
  approvedByYearOfStudy?: YearOfStudyCountsDto;
}

export interface NotificationDto {
  id:number;
  isPublished: number;
  isRead: number;
  applicant: PersonalDetailsDto;
  updateDate: Date;
  applicationId: number;
  applicationStage: APPLICATION_STAGE;
}

export interface StatusDto {
  name: string;
  value: number;
}

/** Matches API `YearOfStudyCountsDto` (dashboard pipeline breakdown by study year). */
export interface YearOfStudyCountsDto {
  firstYear: number;
  secondYear: number;
  thirdYear: number;
  fourthYear: number;
  pgda: number;
  unknown: number;
}

export const emptyYearOfStudyCounts = (): YearOfStudyCountsDto => ({
  firstYear: 0,
  secondYear: 0,
  thirdYear: 0,
  fourthYear: 0,
  pgda: 0,
  unknown: 0,
});

/** Dashboard “returning students” cohort (same rules as ReturningApplicants count). */
export interface ReturningApplicantRowDto {
  applicationId: number;
  name: string;
  surname: string;
  identityNumber: string;
  gender: string;
  race: string;
  email: string;
  phoneNumber: string;
  applicationStage: string;
  applicationYear: number;
  dateCompleted: string | null;
  isAssessed: boolean;
}
export interface AdminApplicantState extends IState {
  applicants: AllApplicantDto[];
  exportExcel: any;
  updateStatus: boolean;
  applicationYears: number[];
}

export interface ApplicantDocumentState extends IState {
  applicantDocuments: ApplicantDocument;
  createDocument: boolean;
}

export interface ApplicantDocument {
  documents: DocumentsViewDto[];
  guardians: LegalGuardianDetailDto[];
}

export interface AdminApplicantDeclineState extends IState {
  declineReasons: addDeclineReasonDto[];
  declineReason: addDeclineReasonDto;
  deleteDeclineReason: number;
}

export interface AssessmentAuditsState extends IState {
  assessmentAudits: AssessmentAuditsDto[];
  setAssessment: boolean;
}

export interface ApplicationDateState extends IState {
  setApplicatantDates: SetApplicationDatesDto;
  getApplicationDates: SetApplicationDatesViewDto[];
}

export interface AssessmentAuditsDto {
  applicationStatusFrom: string;
  applicationStatusTo: string;
  changeReason: string;
  changeDateTime: Date;
  applicationStage: string;
  firstName: string;
  surname: string;
  identityNumber: string;
}

export type SetApplicationDatesDto = {
  openingDate: Date | null | string;
  closingDate: Date | null | string;
  alertStartDate: Date | null | string;
};

export type SetApplicationDatesViewDto = {
  openingDate: Date;
  closingDate: Date;
  alertStartDate: Date;
  createdDate: Date;
  user: string;
  isActive: boolean;
};

export type AssessDto = {
  applicantId: number;
  reason: string;
  meansTests: MeansTestDto[];
};

export type MeansTestDto = {
  fullName: string;
  identityNumber: string;
  employementStatus: string;
  monthOne: number;
  monthTwo: number;
  monthThree: number;
  threshhold:number
};

export type GuardianAssessementAuditItemDto = {
  fullName: string;
  monthlyIncome: number;
  annualIncome: number;
  identityNumber: string;
  employementStatus: string;
};

export type GuardianAssessementAuditDto = {
  isLegible: boolean;
  combineAnnualIncome: number;
  combinedMonthlyIncome: number;
  guardian: GuardianAssessementAuditItemDto[];
};

export type UpdateApplicationStatusDto = {
  applicationId: number;
  applicationStage: APPLICATION_STAGE;
  applicationStatus: APPLICATION_STAGE;
  universityChoiceId: number;
  declineReasonId: number[];
  comment: string;
  sendEmail: boolean;
};

export type CreateDocumentDto = {
  documents: CreateDocumentOwner[];
  applicantId: number;
};

export type MarkAsRead = {
  notificationId: number;
};

export type CreateDocumentOwner = {
  documentTypeId: number;
  documentOwnerId: number;
  documentOwner: number;
};

export type InitialCreateDocumentOwnerState = {
  documentOwnerId: number;
  documentTypeId: number;
  guardianId: number;
  dependantId: number;
};

export type addDeclineReasonDto = {
  reason: string;
  description: string;
  id: number;
};
